// ============================================================================
// AI PROVIDER REGISTRY - OpenRouter ONLY (multi-key edition)
// ============================================================================
// As of April 2026 the app uses OpenRouter exclusively as the gateway to
// every model. Direct provider APIs (Anthropic, OpenAI, Groq) have been
// removed because:
//
//   1. OpenRouter already routes to all of them seamlessly
//   2. Per-provider keys created confusion ("why does my Claude model fail
//      when I haven't configured Anthropic?")
//   3. Multi-key support is much simpler with a single provider
//
// MULTI-KEY SUPPORT
// -----------------
// The user can configure UP TO 5 OpenRouter API keys. Calls are routed
// round-robin across the available keys. When a key gets rate-limited
// (HTTP 429) it goes into a 60-second cooldown and the round-robin skips
// it. This effectively multiplies throughput by N (one stream per key).
//
// Storage: sessionStorage 'qm_openrouter_keys' as a JSON array.
// Legacy fallback: if the new array is empty, falls back to 'qm_apikey'
// (the single-key entry the app shipped with).
//
// ── SEC-7 KNOWN RISK (2026-04-21 pre-pilot audit) ──────────────────────────
// OpenRouter API keys are persisted in `sessionStorage`, which means they
// are readable by ANY JavaScript running in this origin. A successful XSS
// (or a malicious browser extension with host permissions) can exfiltrate
// every configured key in one `sessionStorage.getItem` call and then rack
// up charges / exfiltrate prompts via our OpenRouter account.
//
// The proper fix is a server-side key proxy: the keys live on the backend,
// the browser calls `/api/ai/proxy/chat/completions`, and the backend
// attaches the Authorization header. A partial scaffold already exists at
// `backend/src/routes/ai-proxy.js`.
//
// Migrating every call site, streaming path, and cost-accounting pipeline
// off direct browser-to-OpenRouter is a larger refactor than this audit
// pass can absorb safely before the 2026-05-03 pilot. So for now we:
//   (a) log a console.warn the first time keys are read,
//   (b) export `KEY_STORAGE_RISK_DOCUMENTED = true` so the Settings /
//       AI Models UI can surface a "move keys to server-side proxy" banner,
//   (c) leave this comment so the next engineer finds the audit ID.
//
// DO NOT remove this comment until the ai-proxy migration actually lands
// and `getOpenRouterKeys()` stops returning a session-storage value.
// ============================================================================

import { applyModelRuntimeProfile, getModelConfig, resolveReasoningEffort } from './model-config';

const OPENROUTER_ENDPOINT = 'https://openrouter.ai/api/v1/chat/completions';
const STORAGE_KEY = 'qm_openrouter_keys';
const LEGACY_KEY = 'qm_apikey';
const COOLDOWN_MS = 60_000; // 1 minute cooldown after a 429
const MAX_KEYS = 5;
const BROWSER_PROVIDER_DEV_FLAG = 'qm_allow_browser_provider_key';

export const BROWSER_OPENROUTER_DISABLED_MESSAGE = 'Browser-direct OpenRouter is disabled outside explicit local development. Use the backend AI gateway.';

// SEC-7 (2026-04-21): flag consumed by the AI Models / Settings UI to render
// a "move keys to server-side proxy" banner. Flip to false only after the
// ai-proxy migration is complete and `getOpenRouterKeys()` no longer reads
// from sessionStorage. See the banner comment at the top of this file.
export const KEY_STORAGE_RISK_DOCUMENTED = true;
export const KEY_STORAGE_RISK = Object.freeze({
    auditId: 'SEC-7',
    risk: 'OpenRouter API keys are in sessionStorage and can be exfiltrated by any XSS in this origin.',
    recommendedFix: 'Route all model calls through the server-side ai-proxy so keys never reach the browser.',
    documentedAt: '2026-04-21',
});

// SEC-7: one-shot console.warn so a developer opening DevTools is
// reminded that keys are live in sessionStorage and an XSS would leak them.
let _sec7WarningEmitted = false;
function emitSec7WarningOnce() {
    if (_sec7WarningEmitted) return;
    _sec7WarningEmitted = true;
    // Only warn in a real browser (noop in SSR / Node tests).
    if (typeof window === 'undefined' || typeof console === 'undefined') return;
    // eslint-disable-next-line no-console
    console.warn(
        '[QuoteMaker][SEC-7] OpenRouter keys are being read from sessionStorage. ' +
        'An XSS in this origin could exfiltrate them. Tracking migration to the ' +
        'server-side ai-proxy (see backend/src/routes/ai-proxy.js).'
    );
}

// ─── Reasoning / prompt-cache kill-switch helpers ──────────────────────
// Two tiny storage-probes kept close to buildLegacyBody so the decision
// logic lives with the body it is about to mutate.
function _isReasoningEffortDisabled() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem('qm_disable_reasoning_effort') === 'true';
        }
    } catch { /* storage unavailable */ }
    return false;
}

function _isPromptCacheDisabled() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem('qm_disable_prompt_cache') === 'true';
        }
    } catch { /* storage unavailable */ }
    return false;
}

// Anthropic's ephemeral prompt cache requires the cached block to be at
// least 1024 tokens. Below that the `cache_control` marker silently no-ops
// (wasting a request slot). We don't have a tokenizer in the browser; use a
// conservative 4-chars-per-token estimate and require ≥ ~4200 characters
// before we attach the marker. Callers can pass the marker anyway; the
// helper strips markers on too-small blocks before sending.
const CACHE_MIN_TOKENS = 1024;
const CACHE_MIN_CHARS_APPROX = CACHE_MIN_TOKENS * 4 + 128; // 4224 chars

function _stripTinyCacheMarkers(messages) {
    if (!Array.isArray(messages)) return messages;
    return messages.map(msg => {
        if (!msg || !Array.isArray(msg.content)) return msg;
        let mutated = false;
        const nextContent = msg.content.map(block => {
            if (!block || typeof block !== 'object') return block;
            if (!block.cache_control) return block;
            const text = typeof block.text === 'string' ? block.text : '';
            if (text.length >= CACHE_MIN_CHARS_APPROX) return block;
            mutated = true;
            const { cache_control: _drop, ...rest } = block;
            return rest;
        });
        return mutated ? { ...msg, content: nextContent } : msg;
    });
}

// ─── Legacy body builder ────────────────────────────────────────────────
// The original, pre-Phase-A body shape. Every call still goes through here
// in Phase A - model-config.js only runs in log-only mode to surface the
// diff between what we currently send and what model-config would propose.
// Phase B flips the return of buildBody() to use the proposed body.
//
// F1 (2026-04-24): forward OpenRouter's `reasoning.effort` passthrough when
// the task's TASK_DEFAULTS entry defines `reasoning_effort` AND the model is
// a reasoning model (Opus 4.7, OpenAI o-series). OpenRouter maps the
// passthrough to Anthropic's `thinking: { type: 'enabled', budget_tokens }`
// on the Anthropic route. Kill switch: qm_disable_reasoning_effort.
//
// F2 (2026-04-24): multi-part content + cache_control pass-through. When
// callers build `messages` with `content: [{type:'text', text, cache_control?}]`
// the markers flow straight through to OpenRouter → Anthropic. Blocks below
// the 1024-token cache floor are stripped of their markers so we don't
// waste a cache slot. Kill switch: qm_disable_prompt_cache strips ALL
// markers before send (callers can also skip building them).
function buildLegacyBody({ model, messages, temperature, maxTokens, responseFormat, tools, tool_choice, task }) {
    // If the cache kill-switch is engaged, strip every cache_control marker
    // before we even look at the payload. Tiny-block markers get stripped
    // unconditionally below to avoid Anthropic's silent-no-op trap.
    let outgoingMessages = messages;
    if (Array.isArray(messages)) {
        if (_isPromptCacheDisabled()) {
            outgoingMessages = messages.map(msg => {
                if (!msg || !Array.isArray(msg.content)) return msg;
                return {
                    ...msg,
                    content: msg.content.map(block => {
                        if (!block || !block.cache_control) return block;
                        const { cache_control: _drop, ...rest } = block;
                        return rest;
                    }),
                };
            });
        } else {
            outgoingMessages = _stripTinyCacheMarkers(messages);
        }
    }

    const body = { model, messages: outgoingMessages, temperature, max_tokens: maxTokens };
    if (responseFormat) body.response_format = responseFormat;
    if (Array.isArray(tools) && tools.length > 0) body.tools = tools;
    if (tool_choice) body.tool_choice = tool_choice;

    // Reasoning-effort passthrough (F1). Only emit on reasoning models AND
    // when the kill-switch is NOT set: non-reasoning models treat it as
    // unknown and some providers 400 on unknown top-level keys.
    // Single source of truth for "what effort, if any" (kill switch + reasoning
    // model + task default). Same resolver the prod gateway path uses, so the
    // two send paths never diverge.
    const effort = resolveReasoningEffort(task, model);
    if (effort) {
        body.reasoning = { effort };
    }

    return body;
}

// In-memory state for the round-robin + cooldown tracker. Lives for the
// lifetime of the page; cleared on full reload.
let lastUsedIdx = -1;
const cooldowns = new Map(); // key string -> ms timestamp when usable again

// ─── Provider record (single entry - OpenRouter only) ──────────────────
export const OPENROUTER_PROVIDER = {
    id: 'openrouter',
    name: 'OpenRouter',
    endpoint: OPENROUTER_ENDPOINT,
    headers: (apiKey) => ({
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': typeof window !== 'undefined' ? window.location.origin : '',
        'X-Title': 'QuoteMaker Platform',
    }),
    transformModel: (modelId) => modelId, // OpenRouter accepts the namespaced ID directly
    // Phase A.5: active family-level runtime profile plus log-only task config diff.
    // The active profile is deliberately narrow: strip/adjust model-family
    // parameters known to break current models. Strict OpenRouter parameter
    // routing is operator opt-in so the default remains availability-first. Broad
    // task-default injection remains log-only until Phase B.
    // Opt in to diff logging with `window.QM_LOG_MODEL_CONFIG_DIFF = true`.
    // Phase B will flip the return to the proposed body for low-stakes tasks.
    buildBody: (args) => {
        const legacy = buildLegacyBody(args);
        const runtime = applyModelRuntimeProfile(legacy, { model: args.model, task: args.task });
        try {
            const proposed = getModelConfig(args.model, args.task, {
                temperature: args.temperature,
                maxTokens: args.maxTokens,
                responseFormat: args.responseFormat,
            });
            if (typeof window !== 'undefined' && window.QM_LOG_MODEL_CONFIG_DIFF) {
                const diff = {};
                const keys = new Set([...Object.keys(legacy), ...Object.keys(proposed)]);
                for (const k of keys) {
                    // Skip keys the model-config layer doesn't own (pass-through fields)
                    if (k === 'model' || k === 'messages' || k === 'tools' || k === 'tool_choice') continue;
                    if (JSON.stringify(legacy[k]) !== JSON.stringify(proposed[k])) {
                        diff[k] = { legacy: legacy[k], proposed: proposed[k] };
                    }
                }
                if (Object.keys(diff).length) {
                    // eslint-disable-next-line no-console
                    console.info(`[ModelConfig diff] ${args.model} / ${args.task || '(no task)'}`, {
                        activeRuntimeProfile: runtime._meta || null,
                        diff,
                    });
                }
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[ModelConfig] diff compute failed', e);
        }
        return runtime;
    },
    parseResponse: (data) => data.choices?.[0]?.message?.content || '',
    parseFinishReason: (data) => data.choices?.[0]?.finish_reason || null, // why: 'length' signals silent truncation
    parseToolCalls: (data) => data.choices?.[0]?.message?.tool_calls || null,
    parseAnnotations: (data) => Array.isArray(data.choices?.[0]?.message?.annotations)
        ? data.choices[0].message.annotations
        : [],
    // Workstream E2 (2026-04-24): extended to capture cache + reasoning
    // token buckets AND OpenRouter's `generation.cost` field. The original
    // fields (tokensIn, tokensOut) are preserved verbatim so every legacy
    // caller keeps working. New fields (cacheCreationTokens, cacheReadTokens,
    // reasoningTokens, costUsd, promptTokens, completionTokens, totalTokens)
    // are purely additive.
    //
    // Why: F1 Wave-1 landed cache_control markers on the outbound side of
    // the call. Without inbound capture we can't prove caching actually
    // happened. The costUsd field closes the estimate-vs-actual loop for
    // the per-project cost column added in E1.
    parseUsage: (data) => {
        const u = data?.usage || {};
        const promptTokens = u.prompt_tokens || 0;
        const completionTokens = u.completion_tokens || 0;
        // Anthropic returns cache_creation_input_tokens + cache_read_input_tokens
        // at the top level of `usage`. OpenAI returns prompt_tokens_details.cached_tokens.
        // OpenRouter normalises most providers into Anthropic's shape but we
        // defend against both orderings.
        const cacheCreationTokens = u.cache_creation_input_tokens
            || u.prompt_tokens_details?.cache_creation_tokens
            || 0;
        const cacheReadTokens = u.cache_read_input_tokens
            || u.prompt_tokens_details?.cached_tokens
            || 0;
        // Opus 4.7 + o-series extended-thinking output tokens land in different
        // sub-objects depending on the provider path.
        const reasoningTokens = u.completion_tokens_details?.reasoning_tokens
            || u.reasoning_tokens
            || 0;
        // OpenRouter surfaces the calculated USD cost at data.usage.cost or
        // data.generation.cost depending on whether accounting was enabled
        // on the key. Either is authoritative: we never second-guess it.
        const costUsdRaw = (typeof u.cost === 'number') ? u.cost
            : (typeof data?.generation?.cost === 'number') ? data.generation.cost
            : null;
        const costUsd = (costUsdRaw != null && Number.isFinite(costUsdRaw)) ? costUsdRaw : null;
        return {
            // Legacy / canonical field names. Do NOT remove, dozens of sites
            // in ai-api.js, pipeline-monitor.js, and the dashboard read these.
            tokensIn: promptTokens,
            tokensOut: completionTokens,
            // New fields (E2). Every one is a plain number or null. No provider
            // normalisation beyond what's in this function.
            promptTokens,
            completionTokens,
            cacheCreationTokens,
            cacheReadTokens,
            reasoningTokens,
            costUsd,
            totalTokens: u.total_tokens || 0,
        };
    },
    supportsModel: () => true,
};

// Legacy compat exports - old code might import these names
export const PROVIDERS = [OPENROUTER_PROVIDER];
export const PROVIDER_BY_ID = { openrouter: OPENROUTER_PROVIDER };

// inferProviderForModel always returns 'openrouter' now - no more direct providers
export function inferProviderForModel(_modelId) {
    return 'openrouter';
}

// ─── Multi-key management ──────────────────────────────────────────────

// WO-C (v5.13.1) / TS-014 (2026-05-28): default backend mode sends AI calls
// through /api/ai/* and ignores browser-held keys without clearing them.
// Browser-direct modes are local-dev only and require both:
//   1. VITE_QM_AI_GATEWAY_MODE='browser' or 'hybrid'
//   2. localStorage.qm_allow_browser_provider_key='true'
// LocalStorage-only rollback flags must never bypass the production gateway.
function _importMetaEnv() {
    try {
        if (typeof import.meta !== 'undefined' && import.meta.env) return import.meta.env;
    } catch { /* meta unavailable */ }
    return {};
}

function _isTestRuntime() {
    try {
        return typeof process !== 'undefined'
            && process.env
            && (process.env.VITEST === 'true' || process.env.NODE_ENV === 'test');
    } catch { /* process unavailable in browser */ }
    return false;
}

function _envValue(name) {
    try {
        if (_isTestRuntime()) {
            const testEnv = globalThis.__QM_TEST_ENV__;
            if (testEnv && testEnv[name] !== undefined && testEnv[name] !== '') return testEnv[name];
        }
    } catch { /* test env unavailable */ }
    const metaValue = _importMetaEnv()[name];
    if (metaValue !== undefined && metaValue !== '') return metaValue;
    try {
        if (typeof window === 'undefined' && typeof process !== 'undefined' && process.env) {
            const processValue = process.env[name];
            if (processValue !== undefined && processValue !== '') return processValue;
        }
    } catch { /* process unavailable in browser */ }
    return undefined;
}

function _envFlagTrue(value) {
    return value === true || value === 'true';
}

function _isLocalDevBuild() {
    if (_envFlagTrue(_envValue('PROD'))) return false;
    if (_envFlagTrue(_envValue('DEV'))) return true;
    const rawMode = _envValue('MODE');
    const mode = typeof rawMode === 'string' ? rawMode.trim().toLowerCase() : '';
    return !!mode && mode !== 'production';
}

function _gatewayMode() {
    const raw = _envValue('VITE_QM_AI_GATEWAY_MODE');
    if (typeof raw === 'string' && raw.trim()) {
        const mode = raw.trim().toLowerCase();
        if (mode === 'browser' || mode === 'hybrid' || mode === 'backend') return mode;
    }
    return 'backend';
}

function _browserKeyEscapeActive() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            if (localStorage.getItem(BROWSER_PROVIDER_DEV_FLAG) === 'true') return true;
        }
    } catch { /* storage unavailable */ }
    return false;
}

export function isBrowserOpenRouterAllowed() {
    const mode = _gatewayMode();
    if (mode !== 'browser' && mode !== 'hybrid') return false;
    return _isLocalDevBuild() && _browserKeyEscapeActive();
}

/**
 * Resolve the effective gateway mode after production-safe dev gating.
 * Exported so ai-api.js can route via the same logic.
 */
export function getAiGatewayMode() {
    const mode = _gatewayMode();
    if (mode === 'backend') return 'backend';
    return isBrowserOpenRouterAllowed() ? mode : 'backend';
}

export function assertBrowserOpenRouterAllowed() {
    if (isBrowserOpenRouterAllowed()) return;
    const err = new Error(BROWSER_OPENROUTER_DISABLED_MESSAGE);
    err.name = 'BrowserOpenRouterDisabledError';
    err.code = 'BROWSER_OPENROUTER_DISABLED';
    throw err;
}

/**
 * Should the frontend read browser-held provider keys at all?
 * True only when the explicit local-dev browser path is active.
 */
function _browserKeysAllowed() {
    return isBrowserOpenRouterAllowed();
}

// v5.13.16: removed the module-load warning that nagged dev-mode users
// about browser-held keys. The migration to backend-side keys (D2/D5) is
// done — end-users have no provider keys at all (apiKey is the 'gateway'
// sentinel), and dev users with `qm_dev_keys=1` have explicitly opted in
// to the browser-direct path. The warning fired on every page reload for
// any dev who'd ever pasted a key in the legacy UI, which was just noise
// (their flow was working). Removed.

/**
 * Read the configured OpenRouter keys from sessionStorage.
 * Returns an array of strings (max MAX_KEYS). Always non-null.
 * Falls back to the legacy single 'qm_apikey' entry if no array is set.
 *
 * WO-C: when the gateway is the active path (default), this returns an empty
 * array. The backend owns the provider key; browser-side key rotation is
 * only relevant when the user has explicitly engaged the browser path.
 */
export function getOpenRouterKeys() {
    if (typeof sessionStorage === 'undefined') return [];
    if (!_browserKeysAllowed()) return []; // default backend mode → no browser keys
    // SEC-7: remind on first read per page load that keys live in
    // session storage (XSS-exfiltratable). Safe-to-call repeatedly.
    emitSec7WarningOnce();
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed
                    .filter(k => typeof k === 'string' && k.trim().length > 0)
                    .slice(0, MAX_KEYS);
            }
        }
    } catch { /* fall through to legacy */ }

    // Legacy fallback: single qm_apikey entry → wrap as array
    try {
        const legacy = sessionStorage.getItem(LEGACY_KEY) || (typeof localStorage !== 'undefined' ? localStorage.getItem(LEGACY_KEY) : '');
        if (legacy && legacy.trim()) return [legacy.trim()];
    } catch { /* noop */ }

    return [];
}

/**
 * Replace the entire list of OpenRouter keys.
 * Clamps to MAX_KEYS and trims/filters empties.
 * Also mirrors the FIRST key to the legacy 'qm_apikey' entry so old call
 * sites that read it directly keep working without modification.
 *
 * WO-C: no-op in default (backend) gateway mode. Keys live on the server.
 * Dev override requires VITE_QM_AI_GATEWAY_MODE=browser/hybrid plus
 * localStorage.qm_allow_browser_provider_key='true'.
 */
export function setOpenRouterKeys(keysArray) {
    if (typeof sessionStorage === 'undefined') return;
    if (!_browserKeysAllowed()) {
        // eslint-disable-next-line no-console
        console.warn('[QuoteMaker][WO-C] setOpenRouterKeys ignored: gateway mode is backend. Enable browser mode to persist.');
        return;
    }
    const clean = (Array.isArray(keysArray) ? keysArray : [])
        .map(k => String(k || '').trim())
        .filter(k => k.length > 0)
        .slice(0, MAX_KEYS);
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
        // Mirror the first key to the legacy single-key entry
        if (clean.length > 0) {
            sessionStorage.setItem(LEGACY_KEY, clean[0]);
        } else {
            sessionStorage.removeItem(LEGACY_KEY);
        }
    } catch (e) {
        console.warn('[OpenRouter] Storage write failed');
    }
    // Reset round-robin counter so new keys start being used immediately
    lastUsedIdx = -1;
}


export function clearAllOpenRouterKeys() {
    // WO-C: clear path bypasses the backend-mode gate so a user can always
    // scrub stored keys from sessionStorage, independent of gateway mode.
    if (typeof sessionStorage !== 'undefined') {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(LEGACY_KEY);
        } catch { /* storage unavailable */ }
    }
    lastUsedIdx = -1;
    cooldowns.clear();
}

/**
 * Get the next available key for a fresh AI call. Round-robin across all
 * configured keys, skipping any that are currently in the rate-limit cooldown
 * window. If every key is cooling down, returns the first key (best-effort).
 *
 * Returns null if NO keys are configured.
 */
export function getNextOpenRouterKey() {
    const keys = getOpenRouterKeys();
    if (keys.length === 0) return null;
    if (keys.length === 1) return keys[0];

    // Try round-robin starting from the next index
    for (let i = 0; i < keys.length; i++) {
        lastUsedIdx = (lastUsedIdx + 1) % keys.length;
        const key = keys[lastUsedIdx];
        if (!isKeyCoolingDown(key)) return key;
    }

    // All keys cooling down - return the one with the soonest expiry
    let earliestKey = keys[0];
    let earliestExpiry = cooldowns.get(earliestKey) || Infinity;
    for (const key of keys) {
        const expiry = cooldowns.get(key) || 0;
        if (expiry < earliestExpiry) {
            earliestExpiry = expiry;
            earliestKey = key;
        }
    }
    return earliestKey;
}

/**
 * Mark a key as rate-limited. It will be skipped in round-robin for COOLDOWN_MS.
 */
export function markKeyRateLimited(key, durationMs = COOLDOWN_MS) {
    if (!key) return;
    cooldowns.set(key, Date.now() + durationMs);
}

function isKeyCoolingDown(key) {
    const expiry = cooldowns.get(key);
    if (!expiry) return false;
    if (Date.now() > expiry) {
        cooldowns.delete(key);
        return false;
    }
    return true;
}


// ─── Legacy compatibility exports ──────────────────────────────────────
// Old call sites use getKeyForProvider/setProviderKey/getProviderKeys.
// Map them to the new multi-key API for OpenRouter, no-op for others.

export function getProviderKeys() {
    const keys = getOpenRouterKeys();
    return keys.length > 0 ? { openrouter: keys[0] } : {};
}

export function setProviderKey(providerId, apiKey) {
    if (providerId !== 'openrouter') return; // ignore - we only support OpenRouter now
    // Non-destructive update: only touch slot 0, preserve slots 1-4 that the
    // user configured via the multi-key UI (Settings modal or AI Models page).
    // The previous implementation replaced the whole array with a single-element
    // array, wiping the user's backup keys every time Settings was saved.
    const existing = getOpenRouterKeys();
    const trimmed = (apiKey || '').trim();
    const next = [...existing];
    if (trimmed) {
        next[0] = trimmed;
    } else if (existing.length > 0) {
        // Clear slot 0 only. getOpenRouterKeys filters empties, so if other
        // slots are populated, one of them becomes the new primary.
        next[0] = '';
    }
    setOpenRouterKeys(next);
}


export function getKeyForProvider(providerId) {
    if (providerId !== 'openrouter') return '';
    return getNextOpenRouterKey() || '';
}

// ============================================================================
// AI-4 KILL SWITCH. LocalStorage `qm_disable_ai` (audit 2026-04-21)
// ----------------------------------------------------------------------------
// WARN: The "4-provider failover" this registry once claimed is HISTORICAL.
// As of April 2026 every AI call goes through OpenRouter. If OpenRouter is
// down on pilot day (RJ Bird 2026-05-03), every AI-powered step fails.
//
// `qm_disable_ai` is the graceful-degradation safety net. When set to the
// string 'true' in localStorage, every low-level AI call (callOpenRouter,
// callOpenRouterVision, callOpenRouterWithTools, callOpenRouterStreaming,
// callEmbedding) short-circuits with a friendly error BEFORE any fetch is
// attempted. The UI catches the error and skips the AI step while non-AI
// features (CRM, Print / Save as PDF, manual scope editing) continue working.
//
// Runbook:
//   localStorage.setItem('qm_disable_ai', 'true'); location.reload();
// Re-enable with:
//   localStorage.removeItem('qm_disable_ai'); location.reload();
//
// See docs/pilot-day-runbook.md for the operator-facing version of this.
// ============================================================================

const AI_KILL_SWITCH_ERROR_MESSAGE = 'AI temporarily disabled: qm_disable_ai flag is set. Non-AI features (CRM, Print / Save as PDF, manual editing) continue to work. Clear the flag in localStorage and reload to restore AI.';

/**
 * Is the qm_disable_ai kill switch engaged?
 * Safe everywhere. Returns false if localStorage is unavailable.
 */
export function isAiDisabled() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem('qm_disable_ai') === 'true';
        }
    } catch { /* storage unavailable */ }
    return false;
}

/**
 * Throw a consistent, friendly error when the kill switch is engaged.
 * Called at the top of every low-level call helper in ai-api.js.
 *
 * The thrown error carries `name: 'AiDisabledError'` so call sites that
 * want to distinguish it from network / rate-limit errors can do so.
 */
export function assertAiEnabled() {
    if (isAiDisabled()) {
        const err = new Error(AI_KILL_SWITCH_ERROR_MESSAGE);
        err.name = 'AiDisabledError';
        err.aiDisabled = true;
        throw err;
    }
}

// Exported so the UI (AIModelsPage banner) can label the state consistently.
export const AI_KILL_SWITCH_MESSAGE = AI_KILL_SWITCH_ERROR_MESSAGE;
